import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Restaurant } from '@shared/types/restaurant';
import { MEITRE_GA_ID } from '@core/config/constants';
import { getAnalyticsEnvironment, ANALYTICS_ENV } from './getEnvironment';
import { loadGA4, loadGTM, loadFacebookPixel } from './scriptLoaders';
import type { AnalyticsContextValue, ConversionData, AnalyticsEvent, PageViewEvent } from './types';
import { AnalyticsContext } from './analyticsContext';
export { useAnalyticsContext } from './analyticsContext';

interface AnalyticsProviderProps {
  children: React.ReactNode;
  restaurant: Restaurant;
}

export function AnalyticsProvider({ children, restaurant }: AnalyticsProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const envRef = useRef(getAnalyticsEnvironment());
  const restaurantGaIdRef = useRef(restaurant.googleAnalyticsKey || '');

  // Keep ref in sync if restaurant changes (unlikely but safe)
  restaurantGaIdRef.current = restaurant.googleAnalyticsKey || '';

  useEffect(() => {
    const env = envRef.current;

    if (env === ANALYTICS_ENV.PRODUCTION) {
      // Meitre global GA (no auto page_view — RouteTracker handles it)
      loadGA4(MEITRE_GA_ID, false);

      // Restaurant-specific scripts
      if (restaurant.googleAnalyticsKey) {
        loadGA4(restaurant.googleAnalyticsKey, false);
      }
      if (restaurant.googleTagManagerKey) {
        loadGTM(restaurant.googleTagManagerKey);
      }
      if (restaurant.facebookPixelKey) {
        loadFacebookPixel(restaurant.facebookPixelKey);
      }
    }

    setIsInitialized(true);
  }, [restaurant.googleAnalyticsKey, restaurant.googleTagManagerKey, restaurant.facebookPixelKey]);

  // Analytics calls into window.gtag / window.fbq are defensively wrapped:
  // third-party shims (content blockers, defective GTM containers, Sentry
  // overrides) can throw synchronously from gtag. Without this wrapper, a
  // call inside a try/catch (e.g. useReservationCommit's error handler)
  // would abort the catch and suppress the user-visible error message.
  // Analytics is observational — its failure must never break the UI.
  const safeCall = (fn: () => void, context: string): void => {
    try {
      fn();
    } catch (err) {
      // Don't escalate to Sentry from inside an analytics helper — the
      // caller may already be in an error path. Console.warn is sufficient
      // for DEV diagnosis; in PROD this just becomes a no-op.
      // eslint-disable-next-line no-console
      console.warn(`[Analytics] ${context} threw:`, err);
    }
  };

  const trackPageView = useCallback((pageView: PageViewEvent) => {
    const env = envRef.current;

    if (env === ANALYTICS_ENV.LOCAL) {
      console.log('[Analytics] PageView:', pageView.path, pageView.title ?? '');
      return;
    }

    if (env === ANALYTICS_ENV.PRODUCTION && window.gtag) {
      safeCall(() => {
        window.gtag!('event', 'page_view', {
          page_path: pageView.path,
          page_title: pageView.title,
          send_to: MEITRE_GA_ID,
          client: 'v2',
        });

        const restaurantGaId = restaurantGaIdRef.current;
        if (restaurantGaId) {
          window.gtag!('event', 'page_view', {
            page_path: pageView.path,
            page_title: pageView.title,
            send_to: restaurantGaId,
          });
        }
      }, 'trackPageView');
    }
  }, []);

  const trackEvent = useCallback((event: AnalyticsEvent) => {
    const env = envRef.current;

    if (env === ANALYTICS_ENV.LOCAL) {
      console.log('[Analytics] Event:', event.name, event.properties ?? {});
      return;
    }

    if (env === ANALYTICS_ENV.PRODUCTION && window.gtag) {
      safeCall(() => {
        window.gtag!('event', event.name, {
          ...event.properties,
          send_to: MEITRE_GA_ID,
          client: 'v2',
        });
      }, `trackEvent(${event.name})`);
    }
  }, []);

  const trackConversion = useCallback((data: ConversionData) => {
    const env = envRef.current;

    if (env === ANALYTICS_ENV.LOCAL) {
      console.log('[Analytics] Conversion:', data);
      return;
    }

    if (env === ANALYTICS_ENV.PRODUCTION) {
      if (window.gtag) {
        safeCall(() => {
          window.gtag!('event', 'reservation_complete', {
            send_to: MEITRE_GA_ID,
            client: 'v2',
            reservation_id: data.reservationId,
            unique_id: data.uniqueId,
            party_size: data.diners,
            date: data.date,
            time: data.time,
            guarantee_type: data.guaranteeType,
            fee: data.fee,
            currency: data.currency,
            is_waiting_list: data.isWaitingList ?? false,
          });

          const restaurantGaId = restaurantGaIdRef.current;
          if (restaurantGaId) {
            window.gtag!('event', 'reserva', {
              event_category: 'meitre',
              send_to: restaurantGaId,
            });
          }
        }, 'trackConversion(gtag)');
      }

      if (window.fbq) {
        safeCall(() => {
          window.fbq!('track', 'Schedule');
        }, 'trackConversion(fbq)');
      }
    }
  }, []);

  const value: AnalyticsContextValue = useMemo(() => ({
    trackEvent,
    trackPageView,
    trackConversion,
    isInitialized,
  }), [trackEvent, trackPageView, trackConversion, isInitialized]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

